import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, ClockIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronRightIcon as ChevronRightIconSolid, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
import { useProject } from '../../contexts/ProjectContext';
import { TaskService, formatTaskFromApi } from '../../services/TaskService';
import { getStatusLabel } from '../../utils/statusConfig';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth,
  startOfYear,
  endOfYear,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachYearOfInterval,
  isSameDay,
  isSameMonth,
  isSameYear,
  addDays,
  addMonths,
  addYears
} from 'date-fns';
import es from 'date-fns/locale/es';

const GanttView = () => {
  const { projects } = useProject();
  const [viewMode, setViewMode] = useState('week'); // week, year - Por defecto: semana
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showTasks, setShowTasks] = useState(false);
  const [projectTasks, setProjectTasks] = useState({});
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [zoomLevel, setZoomLevel] = useState(0.5); // Nivel de zoom: 0.5, 1, 1.5, 2
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Para ajuste inicial
  const [showProjectsPanel, setShowProjectsPanel] = useState(true); // Mostrar/ocultar panel de proyectos
  const scrollContainerRef = useRef(null); // Referencia al contenedor scrolleable
  
  // Filtros de estado para proyectos y tareas
  const [statusFilters, setStatusFilters] = useState({
    pending: true,
    progress: true,
    completed: true
  });

  // Cambiar zoom por defecto cuando cambie el modo de vista
  useEffect(() => {
    // Solo cambiar el zoom si no es la carga inicial
    if (!isInitialLoad) {
      setZoomLevel(0.5); // Zoom por defecto del 50% para semana y año
    }
  }, [viewMode, isInitialLoad]);

  // Cargar tareas cuando se activa el checkbox
  useEffect(() => {
    const loadAllTasks = async () => {
      if (showTasks && projects.length > 0) {
        setLoadingTasks(true);
        const tasksData = {};
        
        // Iterar sobre cada proyecto y obtener sus tareas
        for (const project of projects) {
          try {
            const result = await TaskService.getProjectTasks(project.id);
            if (result.success && result.tasks) {
              tasksData[project.id] = result.tasks.map(formatTaskFromApi);
            } else {
              tasksData[project.id] = [];
            }
          } catch (error) {
            console.error(`Error loading tasks for project ${project.id}:`, error);
            tasksData[project.id] = [];
          }
        }
        
        setProjectTasks(tasksData);
        setLoadingTasks(false);
        
        // Expandir todos los proyectos por defecto
        const expanded = {};
        projects.forEach(project => {
          expanded[project.id] = true;
        });
        setExpandedProjects(expanded);
      } else {
        setProjectTasks({});
        setExpandedProjects({});
      }
    };
    
    loadAllTasks();
  }, [showTasks, projects]);

  // Ajuste automático del zoom y scroll inicial
  useEffect(() => {
    if (!isInitialLoad || projects.length === 0) return;
    
    const allDates = [];
    projects.forEach(project => {
      if (project.initialDate) allDates.push(new Date(project.initialDate));
      if (project.finalDate) allDates.push(new Date(project.finalDate));
    });
    
    if (showTasks) {
      Object.values(projectTasks).forEach(tasks => {
        tasks.forEach(task => {
          if (task.initialDate) allDates.push(new Date(task.initialDate));
          if (task.finalDate) allDates.push(new Date(task.finalDate));
        });
      });
    }
    
    if (allDates.length > 0) {
      const minDate = new Date(Math.min(...allDates));
      const maxDate = new Date(Math.max(...allDates));
      
      // Calcular la fecha central entre el inicio y fin de todos los proyectos
      const centerDate = new Date((minDate.getTime() + maxDate.getTime()) / 2);
      setCurrentDate(centerDate);
      
      // Calcular el zoom óptimo para que todos los proyectos sean visibles
      // En vista año, calculamos cuántos meses hay en el rango
      const monthsDiff = (maxDate.getFullYear() - minDate.getFullYear()) * 12 + 
                         (maxDate.getMonth() - minDate.getMonth()) + 2; // +2 meses de margen
      
      // Asumiendo un ancho de viewport promedio de 1200px para el área de Gantt
      const availableWidth = 1200; // Ajustar según necesidad
      const baseWidth = 80; // Ancho base de celda para vista año
      
      // Solo calcular zoom automático si no hay zoom manual previo
      // Para vista semana y año, usar zoom por defecto del 50%
      if (viewMode === 'week' || viewMode === 'year') {
        setZoomLevel(0.5); // Zoom por defecto del 50%
      } else {
        // Calcular el zoom necesario para que todo quepa en la vista
        const requiredWidth = monthsDiff * baseWidth;
        let optimalZoom = availableWidth / requiredWidth;
        
        // Limitar el zoom entre 0.5 y 2.5
        optimalZoom = Math.max(0.5, Math.min(2.5, optimalZoom));
        
        setZoomLevel(optimalZoom);
      }
      setIsInitialLoad(false);
    }
  }, [projects, projectTasks, showTasks, isInitialLoad]);

  // Calcular fechas según el modo de vista y fechas de proyectos
  const dateRange = useMemo(() => {
    // Obtener todas las fechas de proyectos y tareas
    const allDates = [];
    
    // Fechas de proyectos
    projects.forEach(project => {
      if (project.initialDate) allDates.push(new Date(project.initialDate));
      if (project.finalDate) allDates.push(new Date(project.finalDate));
    });
    
    // Fechas de tareas si están visibles
    if (showTasks) {
      Object.values(projectTasks).forEach(tasks => {
        tasks.forEach(task => {
          if (task.initialDate) allDates.push(new Date(task.initialDate));
          if (task.finalDate) allDates.push(new Date(task.finalDate));
        });
      });
    }
    
    // Si no hay fechas, usar fechas por defecto basadas en currentDate
    if (allDates.length === 0) {
      const today = new Date();
      switch (viewMode) {
        case 'week':
          return {
            start: startOfWeek(currentDate, { weekStartsOn: 1 }),
            end: endOfWeek(currentDate, { weekStartsOn: 1 })
          };
        case 'year':
          return {
            start: startOfYear(currentDate),
            end: endOfYear(currentDate)
          };
        default:
          return {
            start: startOfWeek(currentDate, { weekStartsOn: 1 }),
            end: endOfWeek(currentDate, { weekStartsOn: 1 })
          };
      }
    }
    
    // Encontrar la fecha más temprana y más tardía
    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));
    
    // Agregar margen según el modo de vista
    switch (viewMode) {
      case 'week':
        return {
          start: startOfWeek(addDays(minDate, -7), { weekStartsOn: 1 }), // 1 semana antes
          end: endOfWeek(addDays(maxDate, 7), { weekStartsOn: 1 }) // 1 semana después
        };
      case 'year':
        return {
          start: startOfYear(addYears(minDate, -1)), // 1 año antes
          end: endOfYear(addYears(maxDate, 1)) // 1 año después
        };
      default:
        return {
          start: startOfWeek(addDays(minDate, -7), { weekStartsOn: 1 }),
          end: endOfWeek(addDays(maxDate, 7), { weekStartsOn: 1 })
        };
    }
  }, [currentDate, viewMode, projects, showTasks, projectTasks]);

  // Generar fechas para la línea de tiempo
  const timelineDates = useMemo(() => {
    switch (viewMode) {
      case 'week':
        return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
      case 'year':
        return eachMonthOfInterval({ start: dateRange.start, end: dateRange.end });
      default:
        return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
    }
  }, [dateRange, viewMode]);

  // Calcular ancho de celda según zoom y modo de vista
  const getCellWidth = () => {
    const baseWidth = viewMode === 'week' ? 60 : 80;
    return baseWidth * zoomLevel;
  };

  // Calcular la posición de la línea de "hoy" (en píxeles para que escale con zoom)
  const todayPosition = useMemo(() => {
    const today = new Date();
    const totalItems = timelineDates.length;
    
    if (totalItems === 0) return null;
    
    let todayIndex = -1;
    
    switch (viewMode) {
      case 'week':
        todayIndex = timelineDates.findIndex(date => isSameDay(date, today));
        break;
      case 'year':
        todayIndex = timelineDates.findIndex(date => isSameMonth(date, today));
        break;
      default:
        todayIndex = timelineDates.findIndex(date => isSameDay(date, today));
    }
    
    if (todayIndex === -1) return null;
    
    // Calcular en píxeles en lugar de porcentaje
    const cellWidth = getCellWidth();
    return todayIndex * cellWidth + (cellWidth / 2); // Centro de la celda
  }, [timelineDates, viewMode, zoomLevel]);

  // Centrar el scroll en el día actual después de renderizar
  useEffect(() => {
    if (!scrollContainerRef.current || todayPosition === null) return;
    
    const container = scrollContainerRef.current;
    const containerWidth = container.clientWidth;
    
    // Centrar el scroll en la posición del día actual
    const scrollPosition = todayPosition - (containerWidth / 2);
    
    // Hacer scroll suave al centro
    setTimeout(() => {
      container.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }, 300); // Pequeño delay para asegurar que el DOM esté renderizado
  }, [todayPosition, zoomLevel, viewMode]); // Se ejecuta cuando cambia la posición de "hoy" o el zoom

  // Calcular ancho total del timeline con zoom
  const getTimelineWidth = () => {
    return timelineDates.length * getCellWidth();
  };

  // Calcular posición y ancho de las barras de Gantt (en píxeles para que escale con zoom)
  const calculateBarPosition = (project) => {
    if (!project.initialDate || !project.finalDate) return { left: 0, width: 0 };

    const startDate = new Date(project.initialDate);
    const endDate = new Date(project.finalDate);
    
    // Calcular basado en el modo de vista
    let totalUnits, projectStartUnit, projectDurationUnits;
    
    if (viewMode === 'week') {
      // Para vista semanal, calcular en días
      const totalDays = Math.ceil((dateRange.end - dateRange.start) / (1000 * 60 * 60 * 24));
      const projectStartDay = Math.max(0, Math.ceil((startDate - dateRange.start) / (1000 * 60 * 60 * 24)));
      const projectDuration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      
      totalUnits = totalDays;
      projectStartUnit = projectStartDay;
      projectDurationUnits = projectDuration;
    } else {
      // Para vista anual, calcular en meses
      const totalMonths = timelineDates.length;
      const startMonth = (startDate.getFullYear() - dateRange.start.getFullYear()) * 12 + (startDate.getMonth() - dateRange.start.getMonth());
      const durationMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
      
      totalUnits = totalMonths;
      projectStartUnit = Math.max(0, startMonth);
      projectDurationUnits = Math.max(1, durationMonths);
    }
    
    // Calcular posición y ancho en píxeles
    const cellWidth = getCellWidth();
    const timelineWidth = getTimelineWidth();
    
    const leftPx = (projectStartUnit / totalUnits) * timelineWidth;
    const widthPx = (projectDurationUnits / totalUnits) * timelineWidth;

    return { 
      left: Math.max(0, leftPx), 
      width: Math.max(cellWidth * 0.5, widthPx), // Ancho mínimo de media celda
      usePixels: true // Flag para indicar que usamos píxeles
    };
  };

  // Navegación de fechas
  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    
    switch (viewMode) {
      case 'week':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case 'year':
        newDate = addYears(newDate, direction === 'next' ? 1 : -1);
        break;
      default:
        break;
    }
    
    setCurrentDate(newDate);
  };

  // Funciones de zoom
  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 2.5)); // Máximo 2.5x
  };

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5)); // Mínimo 0.5x
  };

  // Formatear fecha según el modo
  const formatDate = (date) => {
    switch (viewMode) {
      case 'week':
        return format(date, 'dd MMM', { locale: es });
      case 'year':
        return format(date, 'MMM', { locale: es });
      default:
        return format(date, 'dd MMM', { locale: es });
    }
  };

  // Formatear título de la vista
  const formatViewTitle = () => {
    // Obtener todas las fechas de proyectos para mostrar el rango real
    const allDates = [];
    projects.forEach(project => {
      if (project.initialDate) allDates.push(new Date(project.initialDate));
      if (project.finalDate) allDates.push(new Date(project.finalDate));
    });
    
    if (showTasks) {
      Object.values(projectTasks).forEach(tasks => {
        tasks.forEach(task => {
          if (task.initialDate) allDates.push(new Date(task.initialDate));
          if (task.finalDate) allDates.push(new Date(task.finalDate));
        });
      });
    }
    
    if (allDates.length > 0) {
      const minDate = new Date(Math.min(...allDates));
      const maxDate = new Date(Math.max(...allDates));
      
      switch (viewMode) {
        case 'week':
          return `${format(minDate, 'dd MMM', { locale: es })} - ${format(maxDate, 'dd MMM yyyy', { locale: es })}`;
        case 'year':
          return `${format(minDate, 'yyyy', { locale: es })} - ${format(maxDate, 'yyyy', { locale: es })}`;
        default:
          return '';
      }
    }
    
    // Fallback si no hay proyectos
    switch (viewMode) {
      case 'week':
        return `${format(dateRange.start, 'dd MMM', { locale: es })} - ${format(dateRange.end, 'dd MMM yyyy', { locale: es })}`;
      case 'year':
        return format(dateRange.start, 'yyyy', { locale: es });
      default:
        return '';
    }
  };

  // Toggle expandir/contraer proyecto
  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // Toggle filtros de estado
  const toggleStatusFilter = (status) => {
    setStatusFilters(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  // Obtener color según el estado del proyecto
  const getProjectColor = (status) => {
    const colorMap = {
      'pending': 'bg-yellow-500',
      'in-progress': 'bg-green-500',
      'progress': 'bg-green-500',
      'completed': 'bg-blue-500',
      'backlog': 'bg-gray-500',
      'cancelled': 'bg-red-500'
    };
    return colorMap[status] || 'bg-gray-500';
  };

  // Obtener color según el estado de la tarea
  const getTaskColor = (status) => {
    const colorMap = {
      'pending': 'bg-yellow-400',
      'in progress': 'bg-green-400',
      'progress': 'bg-green-400',
      'completed': 'bg-blue-400',
      'backlog': 'bg-gray-400',
      'cancelled': 'bg-red-400'
    };
    return colorMap[status] || 'bg-gray-400';
  };

  // Helper para verificar si un estado debe mostrarse
  const shouldShowStatus = (status) => {
    const normalizedStatus = status === 'in-progress' || status === 'in progress' ? 'progress' : status;
    return statusFilters[normalizedStatus] === true;
  };

  // Crear estructura de filas unificada para proyectos y tareas con filtros
  const ganttRows = useMemo(() => {
    const rows = [];
    
    // Filtrar proyectos según los filtros de estado
    const filteredProjects = projects.filter(project => shouldShowStatus(project.status));
    
    filteredProjects.forEach(project => {
      // Agregar fila del proyecto
      rows.push({
        type: 'project',
        data: project,
        id: `project-${project.id}`
      });
      
      // Si está expandido y hay tareas, agregarlas (también filtradas)
      if (showTasks && expandedProjects[project.id]) {
        const tasks = projectTasks[project.id] || [];
        const filteredTasks = tasks.filter(task => shouldShowStatus(task.status));
        
        filteredTasks.forEach(task => {
          rows.push({
            type: 'task',
            data: task,
            projectId: project.id,
            id: `task-${task.id}`
          });
        });
      }
    });
    
    return rows;
  }, [projects, showTasks, expandedProjects, projectTasks, statusFilters]);

  return (
    <div className="space-y-6">
      {/* Header con controles */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Diagrama de Gantt</h2>
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">{formatViewTitle()}</span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Selector de vista */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Vista:</span>
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                className="input-field py-1 px-3 text-sm"
              >
                <option value="week">Semana</option>
                <option value="year">Año</option>
              </select>
            </div>

            {/* Controles de zoom */}
            <div className="flex items-center space-x-2 border-l border-gray-300 dark:border-gray-600 pl-4">
              <span className="text-sm text-gray-600 dark:text-gray-400">Zoom:</span>
              <button
                onClick={zoomOut}
                disabled={zoomLevel <= 0.5}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Alejar"
              >
                <MagnifyingGlassMinusIcon className="h-4 w-4" />
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[3rem] text-center font-medium">
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                onClick={zoomIn}
                disabled={zoomLevel >= 2.5}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Acercar"
              >
                <MagnifyingGlassPlusIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Navegación */}
            <div className="flex items-center space-x-2 border-l border-gray-300 dark:border-gray-600 pl-4">
              <button
                onClick={() => navigateDate('prev')}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1 text-sm bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
              >
                Hoy
              </button>
              <button
                onClick={() => navigateDate('next')}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Filtros y opciones */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-6">
            {/* Checkbox para mostrar tareas */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="showTasks"
                checked={showTasks}
                onChange={(e) => setShowTasks(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="showTasks" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                Mostrar tareas
              </label>
              {loadingTasks && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              )}
            </div>

            {/* Separador vertical */}
            <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>

            {/* Filtros de estado */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtrar por estado:</span>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="filterPending"
                  checked={statusFilters.pending}
                  onChange={() => toggleStatusFilter('pending')}
                  className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                />
                <label htmlFor="filterPending" className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer flex items-center space-x-1">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span>Pendiente</span>
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="filterProgress"
                  checked={statusFilters.progress}
                  onChange={() => toggleStatusFilter('progress')}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="filterProgress" className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer flex items-center space-x-1">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span>En Progreso</span>
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="filterCompleted"
                  checked={statusFilters.completed}
                  onChange={() => toggleStatusFilter('completed')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="filterCompleted" className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer flex items-center space-x-1">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>Completado</span>
                </label>
              </div>
            </div>
          </div>

          {/* Contador de elementos visibles */}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {ganttRows.filter(row => row.type === 'project').length} proyecto(s)
            {showTasks && ` · ${ganttRows.filter(row => row.type === 'task').length} tarea(s)`}
          </div>
        </div>
      </div>

      {/* Contenedor del Gantt */}
      <div className="card overflow-hidden">
        {/* Header fijo para proyectos */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white">Proyectos</h3>
            <button
              onClick={() => setShowProjectsPanel(!showProjectsPanel)}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title={showProjectsPanel ? "Ocultar panel de proyectos" : "Mostrar panel de proyectos"}
            >
              {showProjectsPanel ? (
                <ChevronDoubleLeftIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              ) : (
                <ChevronDoubleRightIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              )}
            </button>
          </div>
        </div>
        
        <div className="flex h-[calc(100vh-350px)] min-h-[450px]">
          {/* Columna de proyectos - Condicional */}
          {showProjectsPanel && (
            <motion.div 
              initial={{ width: 384 }} // w-96 = 384px
              animate={{ width: 384 }}
              exit={{ width: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex-shrink-0 border-r border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden"
            >
            <div className="flex-1 overflow-y-auto">
              {ganttRows.length > 0 ? (
                ganttRows.map((row, index) => {
                  if (row.type === 'project') {
                    const project = row.data;
                    const tasks = projectTasks[project.id] || [];
                    const isExpanded = expandedProjects[project.id];
                    
                    return (
                      <motion.div
                        key={row.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="p-4 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors h-20 flex items-center"
                      >
                        <div className="flex items-start space-x-3 w-full">
                          {/* Botón para expandir/contraer */}
                          {showTasks && tasks.length > 0 && (
                            <button
                              onClick={() => toggleProjectExpansion(project.id)}
                              className="flex-shrink-0 mt-1 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                            >
                              {isExpanded ? (
                                <ChevronDownIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                              ) : (
                                <ChevronRightIconSolid className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                              )}
                            </button>
                          )}
                          
                          <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${getProjectColor(project.status)}`} />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 dark:text-white line-clamp-2 leading-tight mb-1" title={project.name}>
                              {project.name}
                            </h4>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                project.status === 'in-progress' || project.status === 'progress' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                project.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                project.status === 'completed' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                                'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                              }`}>
                                {getStatusLabel(project.status)}
                              </span>
                              <span className="text-xs text-gray-500">
                                {project.progress || 0}%
                              </span>
                              {showTasks && tasks.length > 0 && (
                                <span className="text-xs text-gray-500">
                                  ({tasks.length} {tasks.length === 1 ? 'tarea' : 'tareas'})
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  } else {
                    // Fila de tarea
                    const task = row.data;
                    
                    return (
                      <motion.div
                        key={row.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="pl-12 pr-4 py-3 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors h-16 flex items-center bg-gray-50/50 dark:bg-gray-800/50"
                      >
                        <div className="flex items-start space-x-3 w-full">
                          <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${getTaskColor(task.status)}`} />
                          <div className="flex-1 min-w-0">
                            <h5 className="text-sm text-gray-700 dark:text-gray-300 line-clamp-1 leading-tight" title={task.name}>
                              {task.name}
                            </h5>
                            <div className="flex items-center space-x-2 mt-0.5">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                task.status === 'in progress' || task.status === 'progress' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                task.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                task.status === 'completed' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                                'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                              }`}>
                                {getStatusLabel(task.status)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  }
                })
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <ClockIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No hay proyectos disponibles</p>
                </div>
              )}
            </div>
            </motion.div>
          )}

          {/* Columna del Gantt */}
          <div 
            ref={scrollContainerRef} 
            className="flex-1 flex flex-col overflow-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#d1d5db #f3f4f6'
            }}
          >
            {/* Header de fechas - Sticky */}
            <div className="sticky top-0 z-20 p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 relative">
              <div className="flex" style={{ width: `${getTimelineWidth()}px` }}>
                {timelineDates.map((date, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 text-center text-sm font-medium text-gray-600 dark:text-gray-400"
                    style={{ 
                      width: `${getCellWidth()}px`
                    }}
                  >
                    {formatDate(date)}
                  </div>
                ))}
              </div>
              
              {/* Línea vertical de "hoy" */}
              {todayPosition !== null && (
                <div 
                  className="absolute top-0 w-0.5 bg-red-500 z-10"
                  style={{ left: `${todayPosition}px`, height: 'calc(100% + 1rem)' }}
                >
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full"></div>
                </div>
              )}
            </div>

            {/* Contenedor de barras de Gantt */}
            <div className="flex-1 relative overflow-auto pt-4 pb-4" style={{ width: `${getTimelineWidth()}px` }}>
              {ganttRows.map((row, index) => {
                if (row.type === 'project') {
                  const project = row.data;
                  const { left, width } = calculateBarPosition(project);
                  
                  return (
                    <motion.div
                      key={row.id}
                      initial={{ opacity: 0, scaleX: 0 }}
                      animate={{ opacity: 1, scaleX: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="h-20 border-b border-gray-200 dark:border-gray-700 relative"
                    >
                      {/* Línea de tiempo de fondo */}
                      <div className="absolute inset-0 flex">
                        {timelineDates.map((_, dateIndex) => (
                          <div
                            key={dateIndex}
                            className="border-r border-gray-200 dark:border-gray-700"
                            style={{ 
                              width: `${getCellWidth()}px`
                            }}
                          />
                        ))}
                      </div>

                      {/* Línea vertical de "hoy" */}
                      {todayPosition !== null && (
                        <div 
                          className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                          style={{ left: `${todayPosition}px` }}
                        >
                          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full"></div>
                        </div>
                      )}

                      {/* Barra del proyecto */}
                      {project.initialDate && project.finalDate && (
                        <div className="relative group">
                          <div
                            className={`absolute top-1/2 transform -translate-y-1/2 h-10 rounded-lg ${getProjectColor(project.status)} opacity-80 hover:opacity-100 transition-all cursor-pointer shadow-md hover:shadow-lg`}
                            style={{
                              left: `${left}px`,
                              width: `${width}px`,
                              minWidth: '40px'
                            }}
                          >
                            {/* Tooltip flotante */}
                            <div className="absolute hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-10">
                              <div className="font-semibold">{project.name}</div>
                              <div className="text-gray-300 mt-1">
                                {new Date(project.initialDate).toLocaleDateString('es-ES')} - {new Date(project.finalDate).toLocaleDateString('es-ES')}
                              </div>
                              <div className="text-gray-300">{project.progress || 0}% completado</div>
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                              </div>
                            </div>

                            {/* Contenido de la barra */}
                            <div className="flex items-center h-full px-2">
                              <span className="text-white text-xs font-medium truncate">
                                {project.name}
                              </span>
                              <span className="ml-auto text-white text-xs font-bold pl-2">
                                {project.progress || 0}%
                              </span>
                            </div>

                            {/* Barra de progreso interno */}
                            {project.progress > 0 && (
                              <div
                                className="absolute inset-0 rounded-lg bg-white/20"
                                style={{
                                  width: `${project.progress}%`,
                                }}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  );
                } else {
                  // Fila de tarea
                  const task = row.data;
                  const taskPosition = calculateBarPosition(task);
                  
                  return (
                    <motion.div
                      key={row.id}
                      initial={{ opacity: 0, scaleX: 0 }}
                      animate={{ opacity: 1, scaleX: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="h-16 border-b border-gray-200 dark:border-gray-700 relative bg-gray-50/50 dark:bg-gray-800/50"
                    >
                      {/* Línea de tiempo de fondo */}
                      <div className="absolute inset-0 flex">
                        {timelineDates.map((_, dateIndex) => (
                          <div
                            key={dateIndex}
                            className="border-r border-gray-200 dark:border-gray-700"
                            style={{ 
                              width: `${getCellWidth()}px`
                            }}
                          />
                        ))}
                      </div>

                      {/* Línea vertical de "hoy" */}
                      {todayPosition !== null && (
                        <div 
                          className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                          style={{ left: `${todayPosition}px` }}
                        >
                          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full"></div>
                        </div>
                      )}

                      {/* Barra de la tarea */}
                      {task.initialDate && task.finalDate && (
                        <div className="relative group">
                          <div
                            className={`absolute top-1/2 transform -translate-y-1/2 h-6 rounded ${getTaskColor(task.status)} opacity-70 hover:opacity-100 transition-all cursor-pointer shadow-sm hover:shadow-md`}
                            style={{
                              left: `${taskPosition.left}px`,
                              width: `${taskPosition.width}px`,
                              minWidth: '30px'
                            }}
                          >
                            {/* Tooltip flotante */}
                            <div className="absolute hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-10">
                              <div className="font-semibold">{task.name}</div>
                              <div className="text-gray-300 mt-1">
                                {new Date(task.initialDate).toLocaleDateString('es-ES')} - {new Date(task.finalDate).toLocaleDateString('es-ES')}
                              </div>
                              <div className="text-gray-300">{getStatusLabel(task.status)}</div>
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                              </div>
                            </div>

                            {/* Contenido de la barra */}
                            <div className="flex items-center h-full px-2">
                              <span className="text-white text-xs truncate">
                                {task.name}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  );
                }
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Leyenda */}
      <div className="card p-4">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Leyenda</h3>
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Proyectos</h4>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Pendiente</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">En Progreso</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Completado</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Backlog</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Cancelado</span>
              </div>
            </div>
          </div>
          {showTasks && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tareas</h4>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Pendiente</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-green-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">En Progreso</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-blue-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Completado</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Backlog</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Cancelado</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Indicador de scroll */}
          <div className="absolute bottom-4 right-4 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 px-3 py-2 rounded-lg shadow-lg text-sm font-medium opacity-75 hover:opacity-100 transition-opacity">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
              </svg>
              <span>Scroll</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttView;


